<script setup lang="ts">
/* __placeholder__ */
import type { PostOrPage, PostsOrPages } from '@tryghost/content-api';
import { useDateFormat } from '@vueuse/core';
import type { ComputedRef } from '@vue/reactivity';

const route = useRoute();

const slug = route.params.slug;

const firstname = ref('');
const lastname = ref('');
const company = ref('');
const contactEmail = ref('');
const emailValidity = ref(true);
const showForm = ref(true);
const verificationMessage = ref('');
const isOpen = ref(false);

const contentRef = ref<HTMLDivElement>();

const authorLinks = [
  {
    name: 'Anupama Sahore',
    link: 'https://www.linkedin.com/in/anupamasahore/',
  },
  {
    name: 'Shivam Kharwal',
    link: 'https://www.linkedin.com/in/shivamkharwal/',
  },
  {
    name: 'Jaya Choudhary',
    link: 'https://www.linkedin.com/in/jaya-choudhary/',
  },
  {
    name: 'Mausmi Ambastha',
    link: 'https://www.linkedin.com/in/mausmiambastha/',
  },
  {
    name: 'Wilson Lobo',
    link: 'https://www.linkedin.com/in/wilson-l/',
  },
  {
    name: 'Manasij Ganguli',
    link: 'https://www.linkedin.com/in/mganguli/',
  },
  {
    name: 'Nikita Kalra',
    link: 'https://www.linkedin.com/in/nikitakalra/',
  },
  {
    name: 'Sourish Bhattacharyya',
    link: 'https://www.linkedin.com/in/nikitakalra/',
  },
];

let intervalID: ReturnType<typeof setInterval>;

let broadcastChannel: BroadcastChannel;

const { pending, data, refresh, error } = await useFetch<{ content: PostOrPage; type: string; available: boolean }>(
  `/api/glossary/${slug}`,
);

watch(
  () => error.value,
  () => {
    if (error.value) {
      throw createError({
        statusCode: 404,
        statusMessage: 'It looks like nothing was found at this location. Maybe try a search?',
      });
    }
  },
  { immediate: true },
);

watch(
  () => data.value,
  async () => {
    const tags = data.value?.content?.tags ?? [];
    const primaryTagSlug = data.value?.content.primary_tag?.slug ?? '';

    const invalidTagSlugs = ['podcast', 'webinar', 'webinar-highlight'];

    const validTagSlugs = ['glossary'];

    const isInvalidLink =
      tags.filter((tag) => invalidTagSlugs.includes(tag.slug)).length > 0 || primaryTagSlug !== validTagSlugs[0];

    if (!data.value || isInvalidLink) {
      throw createError({
        statusCode: 404,
        statusMessage: 'It looks like nothing was found at this location. Maybe try a search?',
      });
    }
  },
  {
    immediate: true,
    deep: true,
  },
);

const { pending: topPostsPending, data: topPosts } = await useFetch<PostsOrPages>('/api/posts', {
  params: {
    limit: '3',
    filter: 'tag:top-blog',
  },
});

const postHtml = computed(() => {
  return data.value?.content.html?.replace(/http:/g, 'https:');
});

const available = computed(() => {
  return data.value?.available;
});

const post = computed(() => {
  return { ...data.value?.content, html: postHtml.value } as PostOrPage;
});

const authorLink = computed(() => {
  const link = authorLinks.find((x) => x.name === post.value.primary_author?.name)?.link || '';

  return link;
});

const datePublished = computed(() => {
  if (!post.value) {
    return '';
  }

  const date = post?.value?.published_at?.substring(0, post?.value?.published_at.indexOf('T')) as string;

  return date;
});

function editPostHtml(postHtml: HTMLElement) {
  let unorderedList = postHtml?.getElementsByTagName('ul');
  let orderedList = postHtml?.getElementsByTagName('ol');
  let blockQuotes = postHtml?.getElementsByTagName('blockquote');
  let tables = postHtml?.querySelectorAll('table');
  let fileCardLink = postHtml?.querySelector('.kg-file-card > a');
  fileCardLink?.setAttribute('target', '_blank');
  let links = postHtml?.getElementsByTagName('a');

  for (let i = 0; i < unorderedList.length; i++) {
    let element = unorderedList[i];
    element.style.listStyleType = 'disc';
  }

  for (let i = 0; i < orderedList.length; i++) {
    let element = orderedList[i];
    element.style.listStyleType = 'decimal';
  }

  for (let i = 0; i < blockQuotes.length; i++) {
    let element = blockQuotes[i];
    let elementInnerHtml = element.innerHTML;

    elementInnerHtml = elementInnerHtml.replace(
      '<a',
      '<a class="text-[#db3f60] text-base md:text-[1.375rem] underline underline-offset-4 decoration-2" aria-label="Post-Link"',
    );

    element.innerHTML = elementInnerHtml;
    element.classList.add('border-l-4');
    element.classList.add('border-[#FF1A75]');
    element.classList.add('italic');
  }

  for (let i = 0; i < tables.length; i++) {
    let element = tables[i];
    element.classList.add('table-fixed');
    element.classList.add('w-full');
  }

  Array.from(links).forEach((link) => {
    if (!link.classList.contains('kg-file-card-container')) {
      link.classList.add('text-[#db3f60]');
    }
  });

  const figureCaption = document.querySelector('.kg-card-hascaption > figcaption');
  figureCaption?.classList.add('basis-full');
  figureCaption?.classList.add('text-center');

  const videoIframes = document.querySelectorAll('.kg-embed-card > iframe') as NodeListOf<HTMLElement>;
  videoIframes?.forEach((iframe: HTMLElement) => {
    iframe.style.width = '100%';
    iframe.style.height = '56.25%';
    iframe.style.aspectRatio = '16/9';
  });

  const productCard = document.querySelector('.kg-product-card-title-container') as Element;

  const productRating = document.querySelector('.kg-product-card-rating') as Element;

  const productDescription = document.querySelector('.kg-product-card-description') as Element;

  const parentNode = productCard?.parentNode as ParentNode;

  const wrapper = document.createElement('div');

  wrapper.classList.add('product-wrapper');

  wrapper.append(productCard, productRating);

  parentNode?.insertBefore(wrapper, productDescription);
}

watch(
  () => contentRef.value,
  () => {
    if (contentRef.value) {
      editPostHtml(contentRef.value);
    }
  },
  {
    immediate: true,
  },
);

async function verifyAndShowBlog() {
  broadcastChannel.onmessage = async (event) => {
    if (event.data) {
      if (event.data.is_email_verified === 'verified') {
        refresh();
      } else {
        showForm.value = true;
        return;
      }
    } else {
      showForm.value = true;
      return;
    }
    isOpen.value = false;
    clearInterval(intervalID);
    broadcastChannel.close();
  };

  return;
}

function checkIfVerified(id: string) {
  const cookies = document.cookie;

  if (cookies.includes(`userId=${id}`) && id) {
    refresh();
    return;
  }
  if (!id) {
    verificationMessage.value = 'Please check your inbox for the verification link.';
    isOpen.value = true;
  }

  broadcastChannel = new BroadcastChannel('email_verification_broadcast');

  verifyAndShowBlog();
}

useHead(() => ({
  title: () => `${post.value?.meta_title}`,
  meta: () => [
    { name: 'title', content: `${post.value.meta_title}` },
    { name: 'description', content: `${post.value?.meta_description}` },
    { name: 'image', property: 'og:image', content: `${post.value?.feature_image}` },
    {
      property: 'og:description',
      content: `${post.value?.meta_description}`,
    },
    {
      property: 'og:title',
      content: `${post.value.meta_title}`,
    },
    {
      property: 'og:url',
      content: `https://www.zapscale.com/customer-success-glossary/${post.value.slug}`,
    },
    {
      property: 'og:type',
      content: 'article',
    },
    {
      name: 'twitter:card',
      content: 'summary',
    },
  ],
  link: [
    {
      rel: 'canonical',
      href: 'https://www.zapscale.com' + route.path,
    },
  ],
}));

useJsonld(() => {
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'ZapScale',
      description:
        'A single-source customer success management SaaS platform with 50 pre-built playbooks, 40 out-of-the-box KPIs, and unlimited user logins.',
      url: 'https://www.zapscale.com/',
      logo: 'https://www.zapscale.com/images/Header_logo.svg',
      sameAs: [
        'https://www.facebook.com/zapscale',
        'https://www.linkedin.com/company/zapscale/',
        'https://twitter.com/ZapScale',
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': 'https://www.zapscale.com' + route.path,
      },
      headline: post?.value?.title,
      description: post?.value?.excerpt,
      image: post?.value?.feature_image as string,
      author: {
        '@type': 'Person',
        name: post?.value?.primary_author?.name,
      },
      publisher: {
        '@type': 'Organization',
        name: 'ZapScale',
        logo: {
          '@type': 'ImageObject',
          url: 'https://www.zapscale.com/images/Header_logo.svg',
        },
      },
      datePublished: datePublished.value,
    },
  ];
});

const formatted = computed(() => {
  if (!post.value) {
    return '';
  }
  return useDateFormat(new Date(post.value?.published_at as string), 'D MMMM, YYYY');
}) as ComputedRef;

const noAuthor = computed(() => {
  return post.value.tags?.find((tag) => tag.name === 'noauthor');
});

const blogPostLink = computed(() => {
  if (process.client) {
    return encodeURI(document.location.href);
  }
});

function shareBlog(site: string) {
  let params = `scrollbars=no,resizable=yes,status=no,location=no,toolbar=no,menubar=no,
width=600,height=500,left=400,top=200`;
  let link;
  switch (site) {
    case 'facebook':
      link = `https://www.facebook.com/sharer/sharer.php?u=${blogPostLink.value}&title=Check out this blog on ZapScale!!!&description=&quote=&hashtag=`;
      break;
    case 'linkedin':
      link = `https://www.linkedin.com/shareArticle/?url=${blogPostLink.value}`;
      break;
    case 'twitter':
      link = `https://twitter.com/intent/tweet?text=Check out this blog on ZapScale!!!&url=${blogPostLink.value}`;
      break;
    default:
      return;
  }
  window.open(link, 'share', params);
}

async function onSubmitForm() {
  try {
    const pattern = new RegExp('@+(?!gmail|yahoo|aol|hotmail|outlook|live)');
    let valid = pattern.test(contactEmail.value);

    if (valid) {
      emailValidity.value = true;

      showForm.value = false;

      const { id } = await $fetch('/api/verify', {
        method: 'POST',
        body: {
          firstname: firstname.value,
          lastname: lastname.value,
          email: contactEmail.value,
          company: company.value,
          slug: slug,
        },
      });

      checkIfVerified(id);
    } else {
      emailValidity.value = false;
    }
  } catch (err) {
    console.log('error', err);
    contactEmail.value = '';
  }
}

function closeModal() {
  isOpen.value = false;
}
</script>

<template>
  <div class="bg-[#f9f7f5]">
    <div class="container mx-auto">
      <div class="px-4 pt-2 xl:px-0">
        <div class="flex flex-row gap-x-16 md:px-6">
          <div v-if="!pending" class="block w-full md:max-w-[56vw]">
            <p class="text-base font-bold text-[#db3f60]">CATEGORY > GLOSSARY</p>
            <h1 class="mb-5 mt-2 text-2xl font-extrabold text-[#3a3a3c] md:text-4xl">
              {{ post?.title }}
            </h1>
            <div class="mt-1 flex items-center justify-start gap-x-4 divide-x-2">
              <span class="flex items-center justify-start gap-x-2 text-[#3a3a3c]">
                <Icon name="ph:calendar" size="20" />
                {{ formatted.value }}
              </span>
              <span class="flex items-center justify-start gap-x-2 pl-4">
                <Icon name="ph:clock" size="20" />
                {{ post?.reading_time }} min read
              </span>
            </div>
            <NuxtLink :to="`/blog/${post?.slug}`">
              <figure class="mt-7">
                <NuxtImg
                  :src="post?.feature_image.replace(/http:/g, 'https:')"
                  :alt="post?.title"
                  class="aspect-[191/100]"
                  width="906"
                  height="474"
                  v-if="post?.feature_image"
                />
              </figure>
            </NuxtLink>
            <div class="mt-3">
              <div ref="contentRef" class="prose-base md:prose-lg" v-html="post.html"></div>
            </div>
            <div v-if="!available">
              <div v-if="showForm" class="mx-auto mt-6 w-[90%] border-[0.6px] border-[#3a3a3c] py-7 text-center">
                <p class="mb-4 text-2xl font-extrabold md:text-3xl">Want to read the full blog?</p>
                <p class="mb-5 text-sm font-medium md:text-xl">
                  Sign up to get access to our exclusive content.<br />Don’t worry, we won’t spam you!
                </p>
                <form
                  class="mx-auto flex w-[90%] flex-col items-center gap-y-2 md:w-[52%]"
                  @submit.prevent="onSubmitForm"
                >
                  <input
                    required
                    class="w-full rounded-full px-3 py-3 outline-none"
                    placeholder="First name"
                    id="firstname"
                    name="firstname"
                    v-model="firstname"
                  />
                  <input
                    required
                    class="w-full rounded-full px-3 py-3 outline-none"
                    placeholder="Last name"
                    id="lastname"
                    name="lastname"
                    v-model="lastname"
                  />
                  <input
                    id="work-email-blog"
                    placeholder="Enter your work email"
                    class="w-full rounded-full px-4 py-3 outline-none"
                    type="email"
                    v-model="contactEmail"
                    required
                  />
                  <input
                    required
                    class="w-full rounded-full px-4 py-3 outline-none"
                    placeholder="Where do you work?"
                    id="company"
                    name="company"
                    v-model="company"
                  />
                  <button
                    type="submit"
                    class="mt-3 w-1/2 rounded-full bg-[#3a3a3c] px-4 py-3 font-semibold text-white md:w-3/4 lg:text-lg xl:w-[45%]"
                  >
                    Unlock Content
                  </button>
                </form>
                <p v-if="!emailValidity" class="text-center text-sm font-bold text-[#3a3a3c] md:text-base">
                  Please enter your professional email address.
                </p>
              </div>
            </div>
            <div class="mt-6 flex items-center justify-end border-b-[1px] border-b-[#9d9d9d] border-opacity-20 pb-3">
              <div class="flex items-center gap-x-3 px-3 md:px-0">
                <button title="Share on Facebook" @click="shareBlog('facebook')">
                  <Icon name="la:facebook" size="24" />
                </button>
                <button title="Share on LinkedIn" @click="shareBlog('linkedin')">
                  <Icon name="la:linkedin" size="24" />
                </button>
                <button title="Share on Twitter" @click="shareBlog('twitter')">
                  <Icon name="ri:twitter-x-fill" size="20" />
                </button>
              </div>
            </div>
            <div v-if="!noAuthor" class="border-b-[1px] border-b-[#9d9d9d] border-opacity-20">
              <p class="mt-3 text-base font-bold text-[#db3f60]">ABOUT THE AUTHOR</p>
              <div class="mb-5 mt-2">
                <NuxtImg
                  loading="lazy"
                  class="mb-3 h-14 w-14 rounded-[50%]"
                  :src="post?.primary_author?.profile_image"
                  width="56"
                  height="56"
                  alt="Author profile image"
                  v-if="post?.primary_author?.profile_image"
                />
                <div>
                  <span class="text-xl font-semibold text-[#424242]">{{ post?.primary_author?.name }}</span>
                  <ul class="my-2 flex flex-row justify-start gap-x-3">
                    <li>
                      <NuxtLink
                        v-if="authorLink?.length"
                        :to="authorLink"
                        aria-label="Author-LinkedIn-Profile"
                        target="_blank"
                      >
                        <Icon name="skill-icons:linkedin" size="24" />
                      </NuxtLink>
                    </li>
                    <li>
                      <NuxtLink
                        v-if="post?.primary_author?.twitter"
                        :to="`https://twitter.com/${post?.primary_author?.twitter}`"
                        aria-label="Author-Twitter-Profile"
                        target="_blank"
                      >
                        <Icon name="skill-icons:twitter" size="24" />
                      </NuxtLink>
                    </li>
                  </ul>
                </div>
                <p class="text-base font-normal">{{ post?.primary_author?.bio }}</p>
              </div>
            </div>
            <p class="mt-6 cursor-pointer text-2xl font-extrabold text-[#6DBD77] md:text-4xl">TOP BLOGS</p>
            <div
              id="post__container"
              v-if="topPosts?.length"
              class="no-scrollbar mb-6 mt-6 flex w-full flex-row gap-x-[1%] overflow-x-scroll whitespace-nowrap"
            >
              <LazyBlogPost
                v-if="topPosts?.length"
                class="w-1/3 whitespace-normal"
                v-for="post in topPosts"
                :key="post.id"
                :content="post"
                :content-type="'post'"
                :footer="true"
              />
            </div>
            <LazyScrollArrows
              v-if="topPosts?.length"
              :id="'post__container'"
              :scroll-size="topPosts?.length <= 3 ? topPosts?.length : 5"
            />
          </div>
          <div v-else class="flex min-h-[100vh] w-full items-center justify-center md:max-w-[56vw]">
            <Spinner />
          </div>
          <div class="sticky top-1 hidden self-start md:block md:w-[351px] lg:top-36">
            <p class="text-base font-bold text-[#db3f60]">MORE GLOSSARY TOPICS</p>
            <GlossaryList />
            <form class="mt-8 flex w-full justify-between rounded-full border-2">
              <input
                placeholder="Subscribe for Latest Posts"
                class="w-full rounded-full pl-5 outline-none placeholder:text-xs placeholder:lg:text-sm"
              />
              <button
                type="submit"
                class="-ml-52 h-[2.5rem] w-[2.5rem] rounded-full bg-[#3a3a3c] font-semibold text-white lg:h-[3.063rem] lg:w-[3.063rem]"
              >
                >
              </button>
            </form>
          </div>
        </div>

        <Newsletter class="mt-4" />
      </div>
    </div>
    <div id="dialog" class="relative z-50" v-if="isOpen">
      <div class="fixed inset-0 bg-black/30" aria-hidden="true"></div>

      <div class="fixed inset-0 flex items-center justify-center">
        <div class="relative max-w-[85vw] rounded-3xl bg-white px-[3.875rem] py-10 md:max-w-xl">
          <button @click="closeModal" class="absolute right-4 top-4">
            <svg width="26" height="25" viewBox="0 0 26 25" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M12.5324 0C9.20978 0.00382858 6.02437 1.32528 3.67465 3.67485C1.32508 6.02421 0.00361099 9.20998 0 12.5326C0.688151 29.1581 24.3793 29.1535 25.0649 12.5326C25.0611 9.20978 23.7396 6.02457 21.3901 3.67485C19.0405 1.32528 15.8549 0.00381733 12.5323 0H12.5324ZM12.5324 23.8609V23.8611C9.52894 23.8577 6.64958 22.6631 4.52581 20.5392C2.40213 18.4156 1.20741 15.5361 1.20396 12.5327C1.82618 -2.49628 23.2413 -2.49215 23.8614 12.5327C23.8579 15.5361 22.6632 18.4155 20.5395 20.5392C18.4156 22.6631 15.5361 23.8576 12.5329 23.8611L12.5324 23.8609ZM17.6263 8.2898L13.3834 12.5327L17.6261 16.7754H17.6263C17.8612 17.0106 17.8612 17.3914 17.6263 17.6266C17.3913 17.8615 17.0103 17.8615 16.7753 17.6266L12.5324 13.3837L8.28969 17.6264V17.6266C8.05453 17.8615 7.67371 17.8615 7.43853 17.6266C7.20357 17.3914 7.20357 17.0106 7.43853 16.7754L11.6814 12.5327L7.43874 8.29H7.43853C7.20358 8.05484 7.20358 7.67402 7.43853 7.43884C7.67369 7.20388 8.05472 7.20388 8.2897 7.43884L12.5324 11.6818L16.7751 7.43905V7.43884C17.0115 7.21134 17.3863 7.21497 17.6182 7.4469C17.8502 7.67884 17.8538 8.05365 17.6263 8.29002L17.6263 8.2898Z"
                fill="#363636"
              ></path>
            </svg>
          </button>
          <div>
            <p class="px-3 py-4 text-center text-base font-bold text-[#db3f60] md:text-2xl">
              {{ verificationMessage }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
.no-scrollbar::-webkit-scrollbar {
  display: none;
}
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

:deep(.kg-image-card) {
  @apply flex;
  @apply flex-row;
  @apply justify-center;
}

:deep(.kg-card-hascaption) {
  @apply flex-wrap;
}

:deep(.kg-callout-card) {
  @apply px-6;
  @apply py-4;
  @apply flex;
  @apply flex-row;
  @apply gap-x-4;
  @apply font-semibold;
  @apply text-white;
}

:deep(.kg-callout-card-grey) {
  @apply bg-[#F7F7F9];
  @apply text-black;
}

:deep(.kg-callout-card-blue) {
  @apply bg-[#E7F8FF];
  @apply text-black;
}

:deep(.kg-callout-card-green) {
  @apply bg-[#EAFAEC];
  @apply text-black;
}

:deep(.kg-callout-card-yellow) {
  @apply bg-[#FFF7E8];
  @apply text-black;
}

:deep(.kg-callout-card-red) {
  @apply bg-[#FEE6E8];
  @apply text-black;
}

:deep(.kg-callout-card-pink) {
  @apply bg-[#FFE9F3];
  @apply text-black;
}

:deep(.kg-callout-card-purple) {
  @apply bg-[#F3EBFF];
  @apply text-black;
}

:deep(.kg-callout-card-accent) {
  @apply bg-[#FF1A75];
}

:deep(.kg-callout-card-white) {
  @apply bg-white;
  @apply text-black;
}

:deep(.kg-file-card-metadata) {
  @apply hidden;
}

:deep(.kg-file-card-icon) {
  @apply hidden;
}

:deep(.kg-file-card) {
  @apply bg-[#3a3a3c];
  @apply w-fit;
  @apply h-[3.472rem];
  @apply px-6;
  @apply py-3;
  @apply text-white;
  @apply text-lg;
  @apply font-semibold;
  @apply max-md:mt-3;
  @apply mb-20;
}

:deep(.kg-product-card) {
  @apply w-[90%];
  @apply md:w-4/5;
  @apply mx-auto;
}

:deep(.kg-product-card-container) {
  @apply border-[1px];
  @apply rounded-md;
  @apply p-3;
  @apply md:p-6;
  @apply w-full;
}

:deep(.kg-product-card-image) {
  @apply my-0;
}

:deep(.kg-product-card-title) {
  @apply font-extrabold;
  @apply text-lg;
  @apply md:text-2xl;
  @apply text-[#3a3a3c];
}

:deep(.kg-product-card-rating) {
  @apply flex;
  @apply flex-row;
  @apply items-center;
  @apply justify-center;
  @apply gap-x-1;
}

:deep(.kg-product-card-rating-star) {
  @apply w-3;
  @apply h-3;
  @apply md:w-4;
  @apply md:h-4;
  @apply text-[#3a3a3c];
}

:deep(.kg-product-card-description > p) {
  @apply max-md:text-base;
  @apply my-2;
  @apply text-gray-500;
}

:deep(.kg-product-card-button) {
  @apply bg-[#FF1A75];
  @apply text-white;
  @apply font-bold;
  @apply inline-block;
  @apply w-full;
  @apply py-1;
  @apply rounded-[4px];
  @apply text-center;
  @apply mt-4;
}

:deep(.product-wrapper) {
  @apply flex;
  @apply flex-row;
  @apply items-center;
  @apply justify-between;
  @apply my-4;
  @apply gap-x-3;
}

:deep(.kg-product-card-title) {
  @apply my-0;
}
</style>
